import { FC, useState } from 'react';

import AddressFinder, {
  defaultValues as addressFinderDefaultValues,
  addressFinderSchema,
} from '@eneco/components/AddressFinder/AddressFinder';
import { useAddressFinderQueryStringDefaultValues } from '@eneco/components/AddressFinder/hooks/useAddressFinderQueryStringDefaultValues';
import { typedYupResolver } from '@eneco/hooks/form';
import { useRedirect } from '@eneco/hooks/redirect';
import { Link } from '@eneco/next';
import { Box, Button, ButtonLink, Form, Stack, Text, TextLink } from '@eneco/sparky';
import { AddressFinderRendering } from '@sitecore/types/AddressFinder';
import { LinkFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { EnergyUsagePurposeSelector } from './EnergyUsagePurpose/EnergyUsagePurposeSelector';

const linkExists = (link?: {
  value: LinkFieldValue & { href: string; linktype: string };
  editable?: string;
}): boolean => {
  return !!link?.value?.href || !!link?.editable;
};

interface FormProps {
  city?: string;
  houseNumber?: string;
  houseNumberSuffix?: string;
  postalCode?: string;
  street?: string;
  energyUsagePurpose?: string;
}

const AddressFinderForm: FC<AddressFinderRendering> = ({ fields }) => {
  const addressFinderQueryStringDefaultValues = useAddressFinderQueryStringDefaultValues();
  const redirect = useRedirect();
  const [isSuffixRequired, setIsSuffixRequired] = useState(false);
  const {
    buttonLink,
    secondaryButtonLink,
    secondaryLink,
    disclaimerText,
    addressGroupLabel,
    energyUsagePurposeList,
    energyUsagePurposeDefaultOption,
    energyUsagePurposeRedirectLink,
  } = fields;

  const formSchema = yup.object({
    ...addressFinderSchema(isSuffixRequired).fields,
    ...(energyUsagePurposeList?.value?.requiredMessage
      ? {
          energyUsagePurpose: yup
            .string()
            .required(energyUsagePurposeList.value.requiredMessage)
            .notOneOf(['none'], energyUsagePurposeList.value.requiredMessage),
        }
      : {}),
  });

  const formMethods = useForm<FormProps>({
    defaultValues: {
      ...addressFinderDefaultValues,
      ...addressFinderQueryStringDefaultValues,
      energyUsagePurpose: energyUsagePurposeDefaultOption?.value ?? 'none',
    },
    mode: 'onBlur',
    resolver: typedYupResolver<FormProps>(formSchema),
  });

  const { handleSubmit, control, watch } = formMethods;

  const { energyUsagePurpose } = watch();

  const onSubmit = ({ houseNumber, postalCode, houseNumberSuffix }: FormProps) => {
    // If "Zakelijk" (business) is selected, use redirect link, otherwise use normal button link
    const linkToUse =
      energyUsagePurpose === 'business' && energyUsagePurposeRedirectLink?.value?.href
        ? energyUsagePurposeRedirectLink.value
        : buttonLink?.value;

    redirect({
      queryParams: { postalCode: postalCode?.replace?.(' ', ''), houseNumber, houseNumberSuffix, energyUsagePurpose },
      link: linkToUse,
    });
  };

  const customLabels = {
    houseNumber: fields.houseNumberFormField,
    houseNumberSuffix: fields.houseNumberSuffixFormField,
    postalCode: fields.postalCodeFormField,
    notFoundErrorNotification: fields.notFoundErrorNotification,
    serverErrorNotification: fields.serverErrorNotification,
  };

  return (
    <FormProvider {...formMethods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <AddressFinder
          customLabels={customLabels}
          isSuffixRequired={isSuffixRequired}
          setIsSuffixRequired={setIsSuffixRequired}
          addressGroupLabel={addressGroupLabel?.value ?? 'Adres invoer'}
        />

        <EnergyUsagePurposeSelector control={control} energyUsagePurposeList={energyUsagePurposeList} direction="row" />

        {(disclaimerText?.value || disclaimerText?.editable) && (
          <Box paddingTop="4">
            <Text color="textLowEmphasis">{disclaimerText.value}</Text>
          </Box>
        )}

        {linkExists(buttonLink) && (
          <Box paddingTop="6">
            <Stack
              gap="4"
              alignY="center"
              direction={{ initial: 'column', lg: 'row' }}
              inline={{ initial: false, lg: true }}
              wrap>
              <Button size="regular" type="submit" action="primary">
                {buttonLink?.value.text}
              </Button>
              {linkExists(secondaryButtonLink) && (
                <ButtonLink href={secondaryButtonLink?.value.href} size="regular" action="secondary">
                  {secondaryButtonLink?.value.text}
                </ButtonLink>
              )}
              {!linkExists(secondaryButtonLink) && linkExists(secondaryLink) && (
                <Link linkType={secondaryLink?.value.linktype ?? 'internal'} linkValue={secondaryLink?.value ?? {}}>
                  <TextLink emphasis="high" href={secondaryLink?.value.href}>
                    {secondaryLink?.value.text}
                  </TextLink>
                </Link>
              )}
            </Stack>
          </Box>
        )}
      </Form>
    </FormProvider>
  );
};

export default AddressFinderForm;
